import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { CircularProgress, Icon } from "@mui/material";
import { jsPDF } from "jspdf";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import PdfHeaderEventos from "components/PdfHeaderEventos";
import EmpleadoLayout from "components/LayoutContainers/EmpleadoLayout";
import EditableText from "components/EditableText";
import boxShadows from "assets/theme/base/boxShadows";
import { formatDate, formatMoneyPunto, Money2Text } from "utils/formatters";
import { MessageManager } from "context";

const QUERY_PRESUPUESTO = gql`
  query presupuestoEvento($id: ID!) {
    presupuestoEvento(id: $id) {
      id
      fecha
      vencimiento
      estado
      lugar
      personal
      monto
      presupuestoData
      evento {
        id
        nombreEvento
        fechaEvento
        estado
        clienteEvento {
          id
          nombre
          apellido
          razonSocial
          tipoDocumento
          documento
          tipoCliente
          telefono
          email
        }
      }
      detalles {
        id
        nombre
        itemServicio
        producto { id nombre }
        cantidadAsignada
        precioUnitario
      }
    }
  }
`;

const KNOWN_SERVICES = ["Desayuno", "Almuerzo", "Merienda", "Cena"];
const SERVICE_ORDER = [...KNOWN_SERVICES, "Otros"];

const tiposDocumento = {
    80: "CUIT",
    86: "CUIL",
    96: "DNI",
    99: "SIN",
};

// Pequeño triángulo como bullet que funciona bien en jsPDF (evita caracteres Unicode no soportados)
const TriangleBullet = ({ size = 10, mr = 8, color = "#344767" }) => (
    <span
        style={{
            display: "inline-flex",
            width: size,
            height: size,
            marginRight: mr,
            verticalAlign: "middle",
        }}
    >
        <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true" focusable="false">
            <path fill={color} d="M8 5v14l11-7z" />
        </svg>
    </span>
);

export default function PresupuestoEventoPDFPage() {
    const { id } = useParams();
    const { lg } = boxShadows;
    const wrapperRef = useRef(null);
    const { handleSnackbar } = useContext(MessageManager);

    const [data, setData] = useState(null);
    const [editable, setEditable] = useState(true);
    const [tipoServicio, setTipoServicio] = useState("");
    // Mapa de horarios por servicio (Desayuno/Almuerzo/Merienda/Cena)
    const [horariosMap, setHorariosMap] = useState({});
    const [comensalesStr, setComensalesStr] = useState("1");
    const [descartables, setDescartables] = useState({
        Desayuno: true,
        Almuerzo: true,
        Merienda: true,
        Cena: true,
        Otros: true,
    });

    const [load, { loading }] = useLazyQuery(QUERY_PRESUPUESTO, {
        fetchPolicy: "network-only",
        onCompleted: (res) => {
            const p = res?.presupuestoEvento;
            setData(p || null);
            const rawServices = (p?.detalles || [])
                .map((d) => (d?.itemServicio || "").trim())
                .filter(Boolean);
            const serviciosReconocidos = Array.from(new Set(rawServices.filter((s) => KNOWN_SERVICES.includes(s))));
            const hasOtros = (p?.detalles || []).some((d) => {
                const svc = (d?.itemServicio || "").trim();
                return !svc || !KNOWN_SERVICES.includes(svc);
            });
            const ordered = [
                ...KNOWN_SERVICES.filter((s) => serviciosReconocidos.includes(s)),
                ...(hasOtros ? ["Otros"] : []),
            ];
            // Intentar tomar valores guardados desde evento.presupuestoData (objeto o string JSON)
            let saved = p?.presupuestoData;
            if (typeof saved === "string") {
                try { saved = JSON.parse(saved); } catch { saved = null; }
            }

            // tipoServicio: prioridad a lo guardado, si existe, sino usar el calculado
            const savedTipoServicio = typeof saved?.tipoServicio === "string" && saved.tipoServicio.trim().length
                ? saved.tipoServicio
                : null;
            setTipoServicio(savedTipoServicio || ordered.join(" + "));

            // horariosMap: usar lo guardado si existe; el efecto de serviciosPresentes completará faltantes con "10:00 hs"
            if (saved?.horariosMap && typeof saved.horariosMap === "object") {
                setHorariosMap(saved.horariosMap);
            }

            // comensales: usar lo guardado si existe, como string
            if (saved?.comensales !== undefined && saved?.comensales !== null) {
                setComensalesStr(String(saved.comensales));
            }

            // descartables: prioridad a lo guardado, con fallback true por servicio
            if (saved?.descartables && typeof saved.descartables === "object") {
                const defaults = SERVICE_ORDER.reduce((acc, k) => ({ ...acc, [k]: true }), {});
                setDescartables({ ...defaults, ...saved.descartables });
            }
        },
    });

    const [savePresupuestoPDFdata] = useMutation(gql`
        mutation savePresupuestoPDFdata($input: savePresupuestoPDFdataInput!) {
            savePresupuestoPDFdata(input: $input) {
                id
            }
        }
    `);

    useEffect(() => {
        if (id) load({ variables: { id } });
    }, [id]);

    const detalles = useMemo(() => (Array.isArray(data?.detalles) ? data.detalles : []), [data]);

    const detallesByServicio = useMemo(() => {
        const map = {};
        SERVICE_ORDER.forEach((s) => (map[s] = []));
        detalles.forEach((d) => {
            const service = (d?.itemServicio || "").trim();
            const key = KNOWN_SERVICES.includes(service) && service ? service : "Otros";
            map[key].push(d);
        });
        return map;
    }, [detalles]);

    // Servicios presentes (en orden)
    const serviciosPresentes = useMemo(
        () => SERVICE_ORDER.filter((s) => (detallesByServicio[s] || []).length > 0),
        [detallesByServicio]
    );

    // Inicializa horarios por servicio con "10:00 hs" si no existen
    useEffect(() => {
        if (!serviciosPresentes.length) return;
        setHorariosMap((prev) => {
            const next = { ...prev };
            serviciosPresentes.forEach((s) => {
                if (!next[s]) next[s] = "10:00 hs";
            });
            return next;
        });
    }, [serviciosPresentes]);

    const baseTotal = useMemo(() => {
        //Sumar precios unitarios * cantidades asignadas de todos los detalles
        return detalles.reduce((sum, d) => {
            const precio = Number(d?.precioUnitario) || 0;
            const cantidad = Number(d?.cantidadAsignada) || 0;
            return sum + precio * cantidad;
        }, 0);
        // return data?.monto || 0;
    }, [detalles, data]);
    console.log("data monto:", data);
    console.log("Base total:", baseTotal);
    const comensales = useMemo(() => {
        const n = Number((comensalesStr || "").replace(/[^0-9]/g, ""));
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [comensalesStr]);

    const precioPorPersona = useMemo(() => {
        if (!comensales || comensales <= 0) return null;
        return baseTotal / comensales;
    }, [baseTotal, comensales]);

    const destinatario = useMemo(() => {
        const cli = data?.evento?.clienteEvento;
        if (!cli) return "-";
        if ((cli?.tipoCliente || "").toLowerCase() === "empresa") {
            const razon = cli?.razonSocial || "-";
            const doc = [cli?.tipoDocumento, cli?.documento].filter(Boolean).join(" ");
            return `${razon}${doc ? ` - ${doc}` : ""}`;
        }
        const nombreCompleto = cli?.nombre || "-"; // nombre ya contiene nombre y apellido
        const doc = [tiposDocumento[cli?.tipoDocumento], cli?.documento].filter(Boolean).join(" ");
        return `${nombreCompleto}${doc ? ` - ${doc}` : ""}`;
    }, [data]);

    const handleToggleDescartables = (svc) =>
        setDescartables((prev) => ({ ...prev, [svc]: !prev[svc] }));


    const guardarImprimir = async () => {
        if (!data?.id) return;
        try {
            await savePresupuestoPDFdata({
                variables: {
                    input: {
                        id: data?.id,
                        fileData: {
                            tipoServicio,
                            horariosMap,
                            comensales: comensalesStr,
                            descartables,
                        },
                    },
                },
            });

            handleGeneratePdf();

        } catch (error) {
            console.error("Error al guardar los datos del presupuesto para PDF:", error);
            handleSnackbar("Error al guardar los datos del presupuesto para PDF", { variant: "error" });
        }
    }

    const handleGeneratePdf = async () => {

        if (!wrapperRef.current) return;
        setEditable(false);

        // Ocultar elementos específicos para el PDF
        const toggleButtons = wrapperRef.current.querySelectorAll('[data-toggle-button]');
        const editableElements = wrapperRef.current.querySelectorAll('[contenteditable="true"]');

        // Guardar estilos originales y aplicar estilos para PDF
        const originalStyles = [];
        toggleButtons.forEach((btn, index) => {
            originalStyles[index] = btn.style.display;
            btn.style.display = 'none';
        });

        const originalBgColors = [];
        editableElements.forEach((el, index) => {
            originalBgColors[index] = el.style.backgroundColor;
            el.style.backgroundColor = 'transparent';
        });

        const element = wrapperRef.current.childNodes[0];
        const doc = new jsPDF({ format: "a4", unit: "px", orientation: "portrait", hotfixes: ["px_scaling"] });

        // Ajuste de altura para evitar una página extra en blanco por márgenes
        const topMargin = 50;
        const bottomMargin = 50;
        const pageHeight = doc.internal.pageSize.getHeight();
        // Resta 2-3px por redondeos internos de html2canvas/jspdf que pueden forzar una página extra
        const targetMinHeight = pageHeight - topMargin - bottomMargin - 3; // área útil efectiva
        const originalMinHeight = element.style.minHeight;
        const originalOverflow = element.style.overflow;
        element.style.minHeight = `${targetMinHeight}px`;
        element.style.overflow = "hidden";

        await doc.html(element, {
            margin: [topMargin, 0, bottomMargin, 0],
            autoPaging: "text",
            callback(pdf) {
                pdf.save(`presupuesto-evento-${data?.id || "N-A"}.pdf`);

                // Restaurar estilos originales
                toggleButtons.forEach((btn, index) => {
                    btn.style.display = originalStyles[index];
                });

                editableElements.forEach((el, index) => {
                    el.style.backgroundColor = originalBgColors[index];
                });

                // Restaurar minHeight original del contenedor principal
                element.style.minHeight = originalMinHeight;
                element.style.overflow = originalOverflow;

                setEditable(true);
            },
        });
    };

    const fechaPresupuesto = data?.fecha ? formatDate(data.fecha, "dd/MM/yyyy") : "-";
    const fechaEvento = data?.evento?.fechaEvento ? formatDate(data.evento.fechaEvento, "dd/MM/yy") : "-";

    if (!data) {
        return (
            <EmpleadoLayout>
                <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column" gap={3}>
                    {loading ? (
                        <>
                            <CircularProgress size={40} color="primary" />
                            <SoftTypography variant="h5">Cargando</SoftTypography>
                        </>
                    ) : (
                        <>
                            <Icon fontSize="large">search_off</Icon>
                            <SoftTypography variant="h5">No se encontró el presupuesto</SoftTypography>
                        </>
                    )}
                </SoftBox>
            </EmpleadoLayout>
        );
    }

    return (
        <EmpleadoLayout>
            <SoftBox p={2} display="flex" flexDirection="column" alignItems="center">
                <div ref={wrapperRef}>
                    <SoftBox
                        sx={{
                            position: "relative",
                            overflow: "hidden",
                            width: "794px",
                            minHeight: "1123px",
                            backgroundColor: "#ffffff",
                            boxShadow: lg,
                            px: 6,
                            pt: 6,
                            pb: 8,
                        }}
                    >
                        <PdfHeaderEventos />

                        {/* Meta superior */}
                        <SoftBox display="flex" alignItems="start" flexDirection="column" mt={2} fontSize="12px" lineHeight={1.7}>
                            <SoftTypography variant="button" fontWeight="bold">
                                Fecha: {fechaPresupuesto}
                            </SoftTypography>
                            <SoftTypography variant="button" fontWeight="bold">
                                Destinatario: {destinatario}
                            </SoftTypography>
                        </SoftBox>

                        {/* Detalle del servicio solicitado */}
                        <SoftBox mt={3} fontSize="12px" lineHeight={1.8}>
                            <SoftTypography variant="button" fontWeight="bold" sx={{ borderTop: "2px solid #584444ff", pt: 1 }} />
                            <SoftTypography variant="button" fontWeight="bold" mt={1} mb={1}>
                                Detalle del servicio solicitado:
                            </SoftTypography>
                            <ul style={{ paddingLeft: 28 }}>
                                <li>
                                    &nbsp;<strong>Tipo de servicio:</strong>&nbsp;
                                    <EditableText
                                        editable={editable}
                                        content={tipoServicio || ""}
                                        handleOnBlur={(e) => setTipoServicio(e.target.innerText)}
                                    />
                                </li>
                                <li>
                                    &nbsp;<strong>Fecha:</strong>&nbsp;{fechaEvento}
                                </li>
                                <li>
                                    &nbsp;<strong>Horarios:</strong>
                                    <SoftBox component="div" sx={{ paddingLeft: '20px' }}>
                                        {serviciosPresentes.map((svc) => (
                                            <SoftBox key={`hr-${svc}`} component="div" sx={{ marginBottom: '1px' }}>
                                                <SoftTypography variant="caption" component="span">
                                                    •&nbsp;{svc}:&nbsp;
                                                    <EditableText
                                                        editable={editable}
                                                        content={horariosMap[svc] || "10:00 hs"}
                                                        handleOnBlur={(e) =>
                                                            setHorariosMap((prev) => ({ ...prev, [svc]: e.target.innerText }))
                                                        }
                                                    />
                                                </SoftTypography>
                                            </SoftBox>
                                        ))}
                                    </SoftBox>
                                </li>
                                <li>
                                    &nbsp;<strong>Cantidad de comensales:</strong>&nbsp;
                                    <EditableText
                                        editable={editable}
                                        content={comensalesStr}
                                        handleOnBlur={(e) => setComensalesStr(e.target.innerText)}
                                    />
                                </li>
                            </ul>
                        </SoftBox>

                        {/* El servicio incluye */}
                        <SoftBox mt={3} fontSize="12px">
                            <SoftTypography variant="button" fontWeight="bold" sx={{ borderTop: "1px solid #ccc", pt: 1 }} />
                            <SoftTypography variant="button" fontWeight="bold" mb={1}>
                                El servicio incluye:
                            </SoftTypography>
                            {console.log(SERVICE_ORDER)}
                            {SERVICE_ORDER.filter((svc) => (detallesByServicio[svc] || []).length > 0).map((svc) => (
                                <SoftBox key={svc} mt={2}>
                                    <SoftBox display="flex" alignItems="center" justifyContent="space-between">
                                        <SoftTypography variant="h6" fontWeight="bold">
                                            {svc}
                                        </SoftTypography>
                                        <SoftButton
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => handleToggleDescartables(svc)}
                                            sx={{ py: 0.25, px: 1, minHeight: 26, display: "flex", alignItems: "center" }}
                                            data-toggle-button="true"
                                        >
                                            <Icon fontSize="inherit" sx={{ fontSize: 16, mr: 0.5 }}>
                                                {descartables[svc] ? "toggle_on" : "toggle_off"}
                                            </Icon>
                                            {descartables[svc] ? "Quitar descartable" : "Incluir descartable"}
                                        </SoftButton>
                                    </SoftBox>
                                    <SoftBox component="div" sx={{ marginTop: '4px', paddingLeft: '20px' }}>
                                        {(detallesByServicio[svc] || []).map((d) => (
                                            <SoftBox key={d?.id} component="div" sx={{ marginBottom: '2px' }}>
                                                <SoftTypography variant="caption" component="span">
                                                    •&nbsp;{d?.nombre || d?.producto?.nombre || "-"} x {d?.cantidadAsignada || 0} unidad(es). $ {formatMoneyPunto(d?.precioUnitario || 0)} c/u.
                                                </SoftTypography>
                                            </SoftBox>
                                        ))}
                                        {descartables[svc] && (
                                            <SoftBox component="div" sx={{ marginBottom: '2px' }}>
                                                <SoftTypography variant="caption" component="span">
                                                    •&nbsp;Descartables.
                                                </SoftTypography>
                                            </SoftBox>
                                        )}
                                    </SoftBox>
                                </SoftBox>
                            ))}
                        </SoftBox>

                        {/* Presupuesto */}
                        <SoftBox mt={3} fontSize="12px" lineHeight={1.8}>
                            <SoftTypography variant="button" fontWeight="bold" sx={{ borderTop: "1px solid #ccc", pt: 1 }} />
                            <SoftTypography variant="button" fontWeight="bold" mt={1} mb={1}>
                                Presupuesto
                            </SoftTypography>

                            <SoftBox mt={1}>

                                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mt={1} width="100%">
                                    <SoftBox display="flex" alignItems="center" width="fit-content">
                                        <TriangleBullet />
                                        <SoftTypography variant="button" minWidth="auto" noWrap>
                                            {serviciosPresentes.join(" + ")}
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox sx={
                                        {
                                            borderBottom: "1px dotted #333",
                                            flexGrow: 1,
                                            marginLeft: "8px",
                                            marginRight: "8px",
                                            width: "100%"
                                        }
                                    }
                                    />
                                    <SoftTypography variant="button">
                                        ${formatMoneyPunto(precioPorPersona || baseTotal)}
                                    </SoftTypography>
                                </SoftBox>

                                {comensales && (
                                    <SoftBox pl={3} mb={2}>
                                        <SoftTypography variant="caption" sx={{ fontStyle: "italic", fontSize: '10px' }}>
                                            ({Money2Text(precioPorPersona || 0).toUpperCase()}) por persona.
                                        </SoftTypography>
                                    </SoftBox>
                                )}

                                {comensales && (
                                    <>

                                        <SoftBox display="flex" alignItems="center" mt={5}>
                                            <SoftBox display="flex" alignItems="center" width="fit-content">
                                                <TriangleBullet />
                                                <SoftTypography variant="button" noWrap>
                                                    TOTAL, por {comensales} personas
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftBox sx={
                                                {
                                                    borderBottom: "1px dotted #333",
                                                    flexGrow: 1,
                                                    marginLeft: "8px",
                                                    marginRight: "8px",
                                                    width: "100%"
                                                }
                                            }
                                            />
                                            <SoftTypography variant="button">
                                                ${formatMoneyPunto(baseTotal)}
                                            </SoftTypography>
                                        </SoftBox>

                                        <SoftBox pl={3}>
                                            <SoftTypography variant="caption" sx={{ fontStyle: "italic", fontSize: '10px' }}>
                                                ({Money2Text(baseTotal).toUpperCase()})
                                            </SoftTypography>
                                        </SoftBox>
                                    </>
                                )}
                            </SoftBox>
                        </SoftBox>

                        <SoftBox
                            bgColor="white"
                            mt={4}
                        >
                            <SoftTypography variant="button" fontWeight="bold">
                                <em>Nota: el precio incluye IVA</em>
                            </SoftTypography>
                        </SoftBox>
                    </SoftBox>
                </div>

                <SoftBox
                    variant="contained"
                    bgColor="white"
                    p={2}
                    style={{
                        position: "fixed",
                        bottom: "1.5rem",
                        right: "1.5rem",
                        zIndex: 1000,
                        boxShadow:
                            "inset 0rem 0rem 1.3rem rgba(0,0,0,0.03), 0rem 0.2rem 0.2rem rgba(0,0,0,0.02), 0rem 0.4rem 0.4rem rgba(0,0,0,0.02)",
                    }}
                >
                    <SoftButton onClick={guardarImprimir} variant="outlined" color="error" disabled={loading}>
                        <Icon fontSize="inherit">download</Icon>&nbsp; Descargar PDF
                    </SoftButton>
                </SoftBox>
            </SoftBox>
        </EmpleadoLayout>
    );
}